import axios, { AxiosResponse } from 'axios';
import { useCallback, useState } from 'react';
import { ContentWatchDto, iSubscriptionPackage__API, iTagShort__API } from '../apiSchema';
import { useData } from '../hooks/useData';
import publicApi from '../publicApi';
import { ContentWatch, iContentWatch } from '../models/ContentWatch';
import { iTag, Tag } from '../models/Tag';
import { iSubscriptionPackage, SubscriptionPackage } from '../models/SubscriptionPackage';

interface iUnsubscribeSettings__API {
    maskedEmail: string;
    settings: ContentWatchDto;
    tags: iTagShort__API[];
    subscriptionPackages: iSubscriptionPackage__API[];
}

interface iUnsubscribeSettings {
    isNotFound: boolean;
    maskedEmail: string;
    settings: ContentWatch;
    tags: iTag[];
    subscriptionPackages: iSubscriptionPackage[];
}

export type UnsubscribeLoadStatus = 'loading' | 'ready' | 'notFound' | 'error';

export type UnsubscribeConfirmStatus = 'idle' | 'saving' | 'saved' | 'notFound' | 'invalid' | 'rateLimited' | 'error';

const emptySettings: iUnsubscribeSettings = {
    isNotFound: false,
    maskedEmail: '',
    settings: new ContentWatch(),
    tags: [],
    subscriptionPackages: []
};

function getStatusCode(error: unknown): number | undefined {
    return axios.isAxiosError(error) ? error.response?.status : undefined;
}

function getSettingsUrl(token: string) {
    return `api/Unsubscribe/${encodeURIComponent(token)}`;
}

async function getUnsubscribeSettings(token: string): Promise<iUnsubscribeSettings> {
    try {
        const response: AxiosResponse<iUnsubscribeSettings__API> = await publicApi.get(getSettingsUrl(token));
        const { maskedEmail, settings, tags, subscriptionPackages } = response.data;

        return {
            isNotFound: false,
            maskedEmail,
            settings: new ContentWatch({ apiModel: settings }),
            tags: tags.map((tag) => new Tag({ apiModel: tag })),
            subscriptionPackages: subscriptionPackages.map((item) => new SubscriptionPackage({ apiModel: item }))
        };
    } catch (error) {
        // useData exposes no status code, so a bad token resolves as data instead of rejecting.
        if (getStatusCode(error) === 404) {
            return { ...emptySettings, isNotFound: true };
        }
        throw error;
    }
}

function getConfirmErrorStatus(error: unknown): UnsubscribeConfirmStatus {
    switch (getStatusCode(error)) {
        case 404:
            return 'notFound';
        case 400:
            return 'invalid';
        case 429:
            return 'rateLimited';
        default:
            return 'error';
    }
}

export default function useUnsubscribeSettings(token: string) {
    const apiData = useData(emptySettings, getUnsubscribeSettings, token);
    const [draft, setDraft] = useState<ContentWatch | null>(null);
    const [confirmStatus, setConfirmStatus] = useState<UnsubscribeConfirmStatus>('idle');

    const data = draft ?? apiData.data.settings;

    const loadStatus: UnsubscribeLoadStatus =
        apiData.isSuccess === false ? 'error' : apiData.isLoading ? 'loading' : apiData.data.isNotFound ? 'notFound' : 'ready';

    const setData = useCallback(
        (subModel: Partial<iContentWatch>) => {
            setDraft(new ContentWatch({ model: { ...data, ...subModel } }));
            setConfirmStatus((status) => (status === 'saving' ? status : 'idle'));
        },
        [data]
    );

    const confirm = useCallback(() => {
        setConfirmStatus('saving');
        publicApi
            .post(`${getSettingsUrl(token)}/confirm`, data.mapForApi())
            .then(() => setConfirmStatus('saved'))
            .catch((error) => setConfirmStatus(getConfirmErrorStatus(error)));
    }, [token, data]);

    return {
        loadStatus,
        maskedEmail: apiData.data.maskedEmail,
        tags: apiData.data.tags,
        subscriptionPackages: apiData.data.subscriptionPackages,
        data,
        setData,
        confirm,
        confirmStatus
    };
}
